import { useEffect, useMemo, useState } from 'react'

import { useCommercialStructure } from 'commons/customHooks'
import i18n from 'commons/libs/i18n'
import { formatDate, getFirstDayOfMonth } from 'commons/utils/date'
import { applyCPFCNPJMask } from 'commons/utils/mask'
import {
  BatchSearch,
  Checkbox,
  DatePicker,
  DSInputType,
  Icon,
  Info,
  Input,
  IntervalPicker,
  Multiselect,
  Select,
  SlimButton,
} from 'components'
import { SelectionTab } from 'components/Filters/Filter/components'
import {
  availableCentersFactories,
  type Channels,
  type Offices,
  OriginOrder,
  YesNoNull,
} from 'constants/enums'
import { statusOvKeys } from 'container/Home/components/Banners/interface'
import { ViewBy, type ViewByType } from 'container/ManageOrders'
import dayjs from 'dayjs'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import type { UserPreferences } from 'services/login/interfaces'
import { getOvTypes } from 'services/manage_ov'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'
import { useDispatch, useSelector } from 'store/store'

import useParams from '../../../commons/customHooks/useParams'
import * as S from './styles'

export type FilterProps = {
  onSubmit: (data: FilterDefaultValues) => void
  viewBy: ViewByType
  setViewBy: (value: ViewByType) => void
  disabledsButtons?: string[]
  onChangeFilter(): void
  onClearFilter?: () => void
}

export interface UrlParams {
  codigosCores?: string
  codigoSapCliente?: string
  codigoTipoOrdem?: string
  codigoZona?: string
  codigosCentros?: string
  codigosMateriais?: string
  codigosStatusEstoque?: string
  codigosStatusOrdemVenda?: string
  codigosStatusPedido?: string
  documentoFatura?: string
  mercado?: string
  escritorioVenda?: string
  equipeVenda?: string
  hierarquia?: string
  materialMTO?: YesNoNull
  caracteristicas?: string
  numeroNotaFiscal?: string
  numeroOrdemVenda?: string
  numeroOrdemVendaSAP?: string
  numeroPedidoCliente?: string
  numeroPedidoRascunho?: string
  numeroPedidoEDI?: string
  page?: string
  searchBox?: string
  periodo?: string
  flagDataFixaEntrega?: YesNoNull
  dataCriacaoPedido?: string
  origem?: OriginOrder
}

const defaultOrderStatusCodes = ['Aprovado', 'Concluído', 'Em aberto', 'Em análise', 'Restrito']

export const caracteristicasOptions: SelectOptions[] = [
  { title: 'HC', value: 0, viewBy: [] },
  { title: 'EDI', value: 1, viewBy: [] },
  { title: 'ND', value: 2, viewBy: [ViewBy.ORDERS, ViewBy.OVS] },
  { title: 'Volume Extra', value: 3, viewBy: [ViewBy.ORDERS, ViewBy.OVS] },
]

export const originOrderOptions: SelectOptions[] = [
  { title: 'Portal', value: OriginOrder.B2B },
  { title: 'Havaconecta', value: OriginOrder.HAVA },
  { title: 'EDI', value: OriginOrder.EDI },
]

const CANCELLED_STATUS_KEY = '90- CANCELADO TOTALMENTE'

export const defaultValues = (params: UrlParams, viewBy: ViewByType) => {
  const startDate = getFirstDayOfMonth()
  const endDate = formatDate(new Date())
  const defaultInterval = `${startDate} - ${endDate}`
  const isViewByOrders = viewBy === ViewBy.ORDERS
  // TODO: Refactor this
  const orderCreationDate =
    params.dataCriacaoPedido &&
    isViewByOrders &&
    !['null', 'Invalid Date'].includes(params.dataCriacaoPedido)
      ? formatDate(params.dataCriacaoPedido, 'YYYY-MM-DDTHH:mm:ss')
      : null

  const preferences = JSON.parse(localStorage.getItem('preferences') || '{}') as UserPreferences
  const { CanalDistribuicao: Mercado, RegiaoVenda, EquipeVenda, EscritorioVenda } = preferences
  const defaultMercado = Mercado ? [Mercado] : []
  const defaultEscritorio = EscritorioVenda ? [EscritorioVenda] : []
  const defaultEquipe = EquipeVenda ? [EquipeVenda] : []
  const defaultCodigoZona = RegiaoVenda ? [RegiaoVenda] : []

  // TODO: Refactor this. Separate the properties according to viewBy
  const formattedDefaultValues = {
    caracteristicas:
      typeof params.caracteristicas === 'string' ? params.caracteristicas.split(',') : [],
    codigoSapCliente: params.codigoSapCliente ?? '',
    codigosCentros:
      typeof params.codigosCentros === 'string' ? params.codigosCentros?.split(',') : [],
    codigosCores: params.codigosCores ?? '',
    codigosMateriais: params.codigosMateriais ?? '',
    codigosStatusEstoque: params.codigosStatusEstoque?.split(',') ?? [],
    codigosStatusOrdemVenda:
      typeof params.codigosStatusOrdemVenda === 'string'
        ? params.codigosStatusOrdemVenda.split(',')
        : [],
    codigosStatusPedido:
      typeof params.codigosStatusPedido === 'string'
        ? params.codigosStatusPedido.split(',')
        : defaultOrderStatusCodes,
    codigoTipoOrdem: params.codigoTipoOrdem ?? '',
    codigoZona:
      typeof params.codigoZona === 'string' ? params.codigoZona?.split(',') : defaultCodigoZona,
    dataCriacaoPedido: orderCreationDate,
    documentoFatura: params.documentoFatura ?? '',
    equipeVenda:
      typeof params.equipeVenda === 'string' ? params.equipeVenda.split(',') : defaultEquipe,
    escritorioVenda:
      typeof params.escritorioVenda === 'string'
        ? params.escritorioVenda.split(',')
        : defaultEscritorio,
    flagDataFixaEntrega: params.flagDataFixaEntrega ?? YesNoNull.NULL, // TODO: Set as null instead of "NULL"
    hideStatusChecked: false,
    hierarquia: params.hierarquia === 'true',
    materialMTO: params.materialMTO ?? YesNoNull.NULL,
    mercado:
      typeof params.mercado && params.mercado === 'string'
        ? params.mercado.split(',')
        : defaultMercado,
    numeroNotaFiscal: params.numeroNotaFiscal ?? '',
    numeroOrdemVenda: params.numeroOrdemVenda ?? '',
    numeroOrdemVendaSAP: params.numeroOrdemVendaSAP ?? '',
    numeroPedidoCliente: params.numeroPedidoCliente ?? '',
    numeroPedidoEDI: params.numeroPedidoEDI ?? '',
    numeroPedidoRascunho: params.numeroPedidoRascunho ?? '',
    origem: typeof params.origem === 'string' ? params.origem?.split(',') : [],
    periodo: params.periodo ?? defaultInterval,
    searchBox: params.searchBox ?? '',
  }

  return formattedDefaultValues
}

export type FilterDefaultValues = ReturnType<typeof defaultValues>

export interface OrderTypesProps {
  ds_tipo_orde: string
  cd_tipo_orde: string
}

type SelectOptions = {
  title: string
  value: string | number
  viewBy?: ViewByType[]
}

export const Filter = ({
  onSubmit,
  viewBy,
  setViewBy,
  disabledsButtons = [],
  onChangeFilter,
  onClearFilter,
}: FilterProps) => {
  const dispatch = useDispatch()

  const [loading, setIsLoading] = useState<boolean>(false)
  const [ovsTypes, setOvsTypes] = useState([])
  const [ignoreParams, setIgnoreParams] = useState(false)

  const { clearParamsExcept, getParams } = useParams()
  const materialType: SelectOptions[] = [
    { title: 'Todos', value: YesNoNull.NULL },
    { title: 'MTO', value: YesNoNull.TRUE },
    { title: 'Padrão', value: YesNoNull.FALSE },
  ]

  const getOvTypesList = async () => {
    setIsLoading(true)
    await getOvTypes()
      .then((response) => {
        const types = response?.data.map((item: OrderTypesProps) => ({
          title: item.ds_tipo_orde,
          value: item.cd_tipo_orde,
        }))

        types.unshift({ title: 'Todos', value: '' })
        setOvsTypes(types)
      })
      .catch((error) => toast.error(error.message))
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    getOvTypesList()
  }, [])

  const { preferences } = useSelector((state) => state.auth)
  const {
    offices: officesCommercialStructure,
    regionals: regionalsCommercialStructure,
    zones: zonesCommercialStructure,
    hasCommercialStructure,
  } = useSelector((state) => state.shared)

  const {
    getMarkets,
    getOffices,
    getOfficesByMarkets,
    getRegionalsByOffices,
    getZonesByRegionals,
  } = useCommercialStructure()

  useEffect(() => {
    if (!hasCommercialStructure) dispatch(getCommercialStructure())
  }, [hasCommercialStructure])

  const params = getParams()

  const filterSelectedOV = (value: Array<string>) => {
    const number = statusOvKeys.map((item) => Number(item.trim().split('-')[0]))
    const valueFormated = value.map((item) => Number(item.trim().split('-')[0]))

    if (value.length) {
      const returnValue = valueFormated.map((item: number) => {
        const index = number.indexOf(item)
        if (index > -1) {
          return statusOvKeys[index]
        }
        return
      })

      return returnValue
    }
    return []
  }

  const restoreStructure = () => {
    const { mercado, escritorioVenda, equipeVenda, codigoZona } = getParams()

    if (mercado) setValue('mercado', mercado.split(','))
    else {
      setValue(
        'mercado',
        getMarkets().map((market) => market.id),
      )
      return
    }

    if (escritorioVenda) setValue('escritorioVenda', escritorioVenda.split(','))
    else {
      const offices = getOffices()

      setValue(
        'escritorioVenda',
        offices.map((office) => office.id),
      )

      return
    }

    if (equipeVenda) setValue('equipeVenda', equipeVenda.split(','))
    if (codigoZona) setValue('codigoZona', codigoZona.split(','))
  }

  const methods = useForm({
    defaultValues: defaultValues(params, viewBy),
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { isDirty },
  } = methods

  const codSapClie = watch('codigoSapCliente')

  useEffect(() => {
    if (codSapClie === '') setValue('hierarquia', false)
  }, [codSapClie])

  const watchNonPrioritary = watch([
    'codigosCores',
    'codigoSapCliente',
    'codigoTipoOrdem',
    'codigosCentros',
    'codigosMateriais',
    'codigosStatusEstoque',
    'codigosStatusOrdemVenda',
    'codigosStatusPedido',
    'hierarquia',
    'materialMTO',
    'caracteristicas',
    'searchBox',
  ])

  const draftNumber = watch('numeroPedidoRascunho')
  const orderNumber = watch('numeroOrdemVenda')
  const orderSAPNumber = watch('numeroOrdemVendaSAP')
  const clientOrderNumber = watch('numeroPedidoCliente')
  const invoiceDocument = watch('documentoFatura')
  const invoiceNumber = watch('numeroNotaFiscal')
  const selectedMarkets = watch('mercado') as Channels[]
  const selectedOffices = watch('escritorioVenda')
  const selectedRegions = watch('equipeVenda')
  const selectedZones = watch('codigoZona')
  const numeroPedidoEDI = watch('numeroPedidoEDI')
  const codigosStatusOrdemVenda = watch('codigosStatusOrdemVenda')
  const hideStatusChecked = watch('hideStatusChecked')

  const clearPrioritary = () => {
    setValue('numeroPedidoRascunho', '')
    setValue('numeroOrdemVenda', '')
    setValue('numeroOrdemVendaSAP', '')
    setValue('numeroPedidoCliente', '')
    setValue('documentoFatura', '')
    setValue('numeroNotaFiscal', '')
    setValue('numeroPedidoEDI', '')
  }

  const prioritary = [
    draftNumber !== '',
    orderNumber !== '',
    orderSAPNumber !== '',
    clientOrderNumber !== '',
    invoiceDocument !== '',
    invoiceNumber !== '',
    numeroPedidoEDI !== '',
  ]

  const isPrioritaryFilled = prioritary.some((value) => value)

  const markets = useMemo(() => {
    const marketsByOffices = getMarkets()
    return marketsByOffices
  }, [selectedMarkets])

  const offices = useMemo(() => {
    const officesByMarkets = getOfficesByMarkets(selectedMarkets)
    return officesByMarkets
  }, [selectedMarkets, officesCommercialStructure])

  const regionals = useMemo(() => {
    const regionalsByOffices = getRegionalsByOffices(selectedOffices as Offices[])
    return regionalsByOffices
  }, [selectedOffices, regionalsCommercialStructure])

  const zones = useMemo(() => {
    const zonesByRegionals = getZonesByRegionals(selectedRegions)
    return zonesByRegionals
  }, [selectedRegions, zonesCommercialStructure])

  useEffect(() => {
    // NOTE: preferences.CanalDistribuicao (mercado)
    if (!preferences.CanalDistribuicao && !params.mercado && !isPrioritaryFilled) {
      setValue(
        'mercado',
        markets.map((market) => market.id),
      )
    }
  }, [preferences.CanalDistribuicao, params.mercado])

  useEffect(() => {
    if (params.mercado) {
      setValue('mercado', params.mercado.split(','))
    }
  }, [params.mercado])

  useEffect(() => {
    if (ignoreParams || (!preferences.EscritorioVenda && !params.escritorioVenda)) {
      setValue(
        'escritorioVenda',
        offices.map((office) => office.id),
      )
    } else if (selectedOffices.length) {
      setValue('escritorioVenda', selectedOffices)
    }
  }, [selectedMarkets])

  useEffect(() => {
    if (ignoreParams || (!preferences.EquipeVenda && !params.equipeVenda)) {
      setValue(
        'equipeVenda',
        regionals.map((regional) => regional.id),
      )
    } else if (selectedRegions.length) {
      setValue('equipeVenda', selectedRegions)
    }
  }, [selectedOffices])

  useEffect(() => {
    if (ignoreParams || (!preferences.RegiaoVenda && !params.codigoZona)) {
      setValue(
        'codigoZona',
        zones.map((zone) => zone.id),
      )
    } else if (selectedZones.length) {
      setValue('codigoZona', selectedZones)
    }

    if (ignoreParams) {
      setIgnoreParams(false)
    }
  }, [selectedRegions])

  useEffect(() => {
    if (isPrioritaryFilled && isDirty) {
      clearPrioritary()
    }
  }, [JSON.stringify(watchNonPrioritary)])

  useEffect(() => {
    if (!isPrioritaryFilled && isDirty) {
      restoreStructure()
    }
  }, [isPrioritaryFilled])

  const resetValues = {
    caracteristicas: [],
    codigoSapCliente: '',
    codigosCentros: [],
    codigosCores: '',
    codigosMateriais: '',
    codigosStatusEstoque: [],
    codigosStatusOrdemVenda: [],
    codigosStatusPedido: defaultOrderStatusCodes,
    codigoTipoOrdem: '',
    codigoZona: [],
    dataCriacaoPedido: null,
    documentoFatura: '',
    equipeVenda: [],
    escritorioVenda: [],
    flagDataFixaEntrega: YesNoNull.NULL,
    hideStatusChecked: false,
    hierarquia: false,
    materialMTO: YesNoNull.NULL,
    mercado: [],
    numeroNotaFiscal: '',
    numeroOrdemVenda: '',
    numeroOrdemVendaSAP: '',
    numeroPedidoCliente: '',
    numeroPedidoEDI: '',
    numeroPedidoRascunho: '',
    origemPedido: [],
    searchBox: '',
  }

  useEffect(() => {
    if (draftNumber !== '') {
      reset({
        ...resetValues,
        numeroPedidoRascunho: draftNumber,
      })
    }
  }, [draftNumber])

  useEffect(() => {
    if (orderNumber !== '') {
      reset({
        ...resetValues,
        numeroOrdemVenda: orderNumber,
      })
    }
  }, [orderNumber])

  useEffect(() => {
    if (orderSAPNumber !== '') {
      reset({
        ...resetValues,
        numeroOrdemVendaSAP: orderSAPNumber,
      })
    }
  }, [orderSAPNumber])

  useEffect(() => {
    if (clientOrderNumber !== '') {
      reset({
        ...resetValues,
        numeroPedidoCliente: clientOrderNumber,
      })
    }
  }, [clientOrderNumber])

  useEffect(() => {
    if (invoiceDocument !== '') {
      reset({
        ...resetValues,
        documentoFatura: invoiceDocument,
      })
    }
  }, [invoiceDocument])

  useEffect(() => {
    if (invoiceNumber !== '') {
      reset({
        ...resetValues,
        numeroNotaFiscal: invoiceNumber,
      })
    }
  }, [invoiceNumber])

  useEffect(() => {
    if (numeroPedidoEDI !== '') {
      reset({
        ...resetValues,
        numeroPedidoEDI: numeroPedidoEDI,
      })
    }
  }, [numeroPedidoEDI])

  const labelField = (label: string, name: string, disableLabel?: boolean) => (
    <S.LabelFilter disabled={disabledsButtons.includes(name) || disableLabel}>
      {label}
      {disabledsButtons.includes(name) && (
        <Info>Campo indisponível. Em breve mais informações.</Info>
      )}
    </S.LabelFilter>
  )

  const handleClearFilter = () => {
    clearParamsExcept(['page'])
    reset(defaultValues({}, viewBy))
    restoreStructure()
    onClearFilter ? onClearFilter() : onChangeFilter()
  }

  const isDisabledFilter = (buttonName: string) => disabledsButtons.includes(buttonName)

  const handleIgnoreParams = () => {
    setIgnoreParams(true)
  }

  const getFilteredCaracteristicas = () => {
    return caracteristicasOptions
      .filter((item) => item.viewBy?.includes(viewBy))
      .map((item) => item.title)
  }

  const filteredStatusOvKeys = hideStatusChecked
    ? statusOvKeys.filter((key) => key !== CANCELLED_STATUS_KEY)
    : statusOvKeys

  useEffect(() => {
    const selectedListSize = codigosStatusOrdemVenda.length

    // NOTE: If hideStatusChecked is true, remove the "CANCELADO TOTALMENTE" status and add all the others.
    if (hideStatusChecked) {
      // NOTE: If selectedListSize is 0, remove the "CANCELADO TOTALMENTE" status and add all the others.
      if (selectedListSize === 0) {
        setValue('codigosStatusOrdemVenda', filteredStatusOvKeys)
      } else {
        // NOTE: If selectedListSize is greater than 0, remove the "CANCELADO TOTALMENTE" status and add all the others.
        const selectedWithoutCancelled = codigosStatusOrdemVenda.filter(
          (key) => key !== CANCELLED_STATUS_KEY,
        )

        setValue('codigosStatusOrdemVenda', selectedWithoutCancelled)
      }
    } else {
      // NOTE: If hideStatusChecked is false, add all the status, including the "CANCELADO TOTALMENTE" status.
      setValue('codigosStatusOrdemVenda', statusOvKeys)
    }

    // NOTE: Avoid submitting the form if the priority filter is filled.
    if (!isPrioritaryFilled) {
      // NOTE: Submit the form.
      handleSubmit(onSubmit)()
    }
  }, [hideStatusChecked])

  return (
    <>
      <S.Container onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <S.ContainerFilter>
            <S.WrapperHeader>
              <S.HeaderContainer>
                <S.HeaderItem>
                  <S.WrapTitleWithButton>
                    <h1>Gerenciamento</h1>
                    <S.StructureInputs>
                      <S.SelectorWrapper>
                        <Controller
                          control={control}
                          name="mercado"
                          render={({ field }) => (
                            <Multiselect
                              disabled={isPrioritaryFilled}
                              error={!selectedMarkets.length}
                              height={22}
                              hideSearch
                              label="Mercado"
                              labelOptionsList={markets.reduce(
                                (acc, market) => {
                                  acc[market.id] = market.description
                                  return acc
                                },
                                {} as Record<string, string>,
                              )}
                              onChange={(value) => {
                                handleIgnoreParams()
                                field.onChange(value)
                              }}
                              optionsList={markets.map((market) => market.id)}
                              selectedOptions={
                                Array.isArray(field.value) ? field.value : [field.value]
                              }
                            />
                          )}
                        />
                      </S.SelectorWrapper>
                      <S.SelectorWrapper>
                        <Controller
                          control={control}
                          name="escritorioVenda"
                          render={({ field }) => (
                            <Multiselect
                              disabled={!selectedMarkets.length || isPrioritaryFilled}
                              error={!selectedOffices.length}
                              height={22}
                              hideSearch
                              label="Escritório"
                              labelOptionsList={offices.reduce(
                                (acc, office) => {
                                  acc[office.id] = `${office.id} - ${office.description}`
                                  return acc
                                },
                                {} as Record<string, string>,
                              )}
                              onChange={(value) => {
                                handleIgnoreParams()
                                field.onChange(value)
                              }}
                              optionsList={offices.map((office) => office.id)}
                              selectedOptions={
                                Array.isArray(field.value) ? field.value : [field.value]
                              }
                            />
                          )}
                        />
                      </S.SelectorWrapper>
                      <S.SelectorWrapper>
                        <Controller
                          control={control}
                          name="equipeVenda"
                          render={({ field }) => (
                            <Multiselect
                              disabled={!regionals.length || isPrioritaryFilled}
                              error={!selectedRegions.length}
                              height={22}
                              hideSearch
                              label="Regional"
                              labelOptionsList={regionals.reduce(
                                (acc, regional) => {
                                  acc[regional.id] = `${regional.id} - ${regional.description}`
                                  return acc
                                },
                                {} as Record<string, string>,
                              )}
                              onChange={(value) => {
                                handleIgnoreParams()
                                field.onChange(value)
                              }}
                              optionsList={regionals.map((regional) => regional.id)}
                              selectedOptions={field.value}
                            />
                          )}
                        />
                      </S.SelectorWrapper>
                      <S.SelectorWrapper>
                        <Controller
                          control={control}
                          name="codigoZona"
                          render={({ field }) => (
                            <Multiselect
                              disabled={!zones.length || isPrioritaryFilled}
                              error={!selectedZones.length}
                              height={22}
                              hideSearch
                              label="Zona"
                              labelOptionsList={zones.reduce(
                                (acc, zone) => {
                                  acc[zone.id] = `${zone.id} - ${zone.description}`
                                  return acc
                                },
                                {} as Record<string, string>,
                              )}
                              onChange={(value) => {
                                field.onChange(value)
                              }}
                              optionsList={zones.map((zone) => zone.id)}
                              selectedOptions={field.value}
                            />
                          )}
                        />
                      </S.SelectorWrapper>
                    </S.StructureInputs>
                  </S.WrapTitleWithButton>
                </S.HeaderItem>
              </S.HeaderContainer>
            </S.WrapperHeader>

            <S.FilterContent>
              <S.FilterContentRow>
                <S.FormField>
                  {labelField('Pedido portal', 'numeroPedidoRascunho')}
                  <Controller
                    control={control}
                    name="numeroPedidoRascunho"
                    render={({ field }) => (
                      <BatchSearch
                        disabled={disabledsButtons.includes('numeroPedidoRascunho')}
                        height={22}
                        onClear={() => {
                          reset({ ...getValues(), numeroPedidoRascunho: '' })
                          restoreStructure()
                        }}
                        placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                        {...field}
                      />
                    )}
                  />
                </S.FormField>
                <S.FormField>
                  {labelField('Nº OV Portal', 'numeroOrdemVenda')}
                  <Controller
                    control={control}
                    name="numeroOrdemVenda"
                    render={({ field }) => (
                      <BatchSearch
                        disabled={isDisabledFilter('numeroOrdemVenda')}
                        height={22}
                        onClear={() => {
                          reset({ ...getValues(), numeroOrdemVenda: '' })
                          restoreStructure()
                        }}
                        placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                        {...field}
                      />
                    )}
                  />
                </S.FormField>
                <S.FormField>
                  {labelField('Nº OV SAP', 'numeroOrdemVendaSAP')}
                  <Controller
                    control={control}
                    name="numeroOrdemVendaSAP"
                    render={({ field }) => (
                      <BatchSearch
                        disabled={isDisabledFilter('numeroOrdemVendaSAP')}
                        height={22}
                        onClear={() => {
                          reset({ ...getValues(), numeroOrdemVendaSAP: '' })
                          restoreStructure()
                        }}
                        placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                        {...field}
                      />
                    )}
                  />
                </S.FormField>
                <S.FormField>
                  {labelField('CPF/CNPJ', 'searchBox')}
                  <Controller
                    control={control}
                    name="searchBox"
                    render={({ field }) => (
                      <Input
                        {...field}
                        design={DSInputType.SEARCH_INPUT}
                        disabled={disabledsButtons.includes('searchBox')}
                        height={22}
                        maxLength={18}
                        onChange={(value) => {
                          field.onChange(applyCPFCNPJMask(value.target.value))
                        }}
                        placeholder="ˍ ˍ.ˍ ˍ ˍ.ˍ ˍ ˍ ˍ/ˍ ˍ ˍ ˍ-ˍˍ"
                        textUppercase
                      />
                    )}
                  />
                </S.FormField>
                <S.FormField>
                  {labelField('Código SAP', 'codigoSapCliente')}
                  <S.ClientSapWrapper>
                    <Controller
                      control={control}
                      name="codigoSapCliente"
                      render={({ field }) => (
                        <BatchSearch
                          disabled={disabledsButtons.includes('codigoSapCliente')}
                          height={22}
                          onClear={() => reset({ ...getValues(), codigoSapCliente: '' })}
                          placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                          {...field}
                        />
                      )}
                    />
                  </S.ClientSapWrapper>
                </S.FormField>
              </S.FilterContentRow>

              <S.FilterContentRow>
                <S.FormField>
                  {labelField('Pedido cliente', 'numeroPedidoCliente')}
                  <Controller
                    control={control}
                    name="numeroPedidoCliente"
                    render={({ field }) => (
                      <BatchSearch
                        disabled={disabledsButtons.includes('numeroPedidoCliente')}
                        height={22}
                        onClear={() => {
                          reset({ ...getValues(), numeroPedidoCliente: '' })
                          restoreStructure()
                        }}
                        placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ "
                        {...field}
                      />
                    )}
                  />
                </S.FormField>
                <S.FormField>
                  {labelField('Material', 'codigosMateriais')}
                  <Controller
                    control={control}
                    name="codigosMateriais"
                    render={({ field }) => (
                      <BatchSearch
                        disabled={disabledsButtons.includes('codigosMateriais')}
                        height={22}
                        onClear={() => reset({ ...getValues(), codigosMateriais: '' })}
                        placeHolder="Buscar por códigos"
                        {...field}
                      />
                    )}
                  />
                </S.FormField>
                <S.FormField>
                  {labelField('Cor', 'codigosCores')}
                  <Controller
                    control={control}
                    name="codigosCores"
                    render={({ field }) => (
                      <BatchSearch
                        {...field}
                        disabled={disabledsButtons.includes('codigosCores')}
                        height={22}
                        onClear={() => reset({ ...getValues(), codigosCores: '' })}
                        placeHolder="Buscar por códigos"
                      />
                    )}
                  />
                </S.FormField>
                <S.FormField>
                  {labelField('Fábrica', 'codigosCentros')}
                  <Controller
                    control={control}
                    name="codigosCentros"
                    render={({ field }) => {
                      return (
                        <Multiselect
                          disabled={disabledsButtons.includes('codigosCentros')}
                          height={22}
                          onChange={(value) => field.onChange(value)}
                          optionsList={availableCentersFactories}
                          selectedOptions={field.value}
                        />
                      )
                    }}
                  />
                </S.FormField>
                <S.FormField>
                  {labelField('Tipo de OV', 'codigoTipoOrdem')}
                  {loading ? (
                    <Controller
                      control={control}
                      name="codigoTipoOrdem"
                      render={({ field }) => (
                        <Select
                          defaultValue=""
                          disabled={true}
                          height={22}
                          name="loadingPlaceholder"
                          options={[]}
                          value={field.name}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      control={control}
                      name="codigoTipoOrdem"
                      render={({ field }) => (
                        <Select
                          defaultValue=""
                          disabled={loading}
                          height={22}
                          name="codigoTipoOrdem"
                          onChange={field.onChange}
                          options={ovsTypes}
                          value={field.value}
                        />
                      )}
                    />
                  )}
                </S.FormField>
              </S.FilterContentRow>

              <S.FilterContentRow>
                {viewBy === ViewBy.ORDERS && (
                  <S.FormField>
                    {labelField('Status Pedido', 'codigosStatusPedido')}
                    <Controller
                      control={control}
                      name="codigosStatusPedido"
                      render={({ field }) => {
                        return (
                          <Multiselect
                            disabled={disabledsButtons.includes('codigosStatusPedido')}
                            height={22}
                            onChange={(value) => field.onChange(value)}
                            optionsList={defaultOrderStatusCodes}
                            selectedOptions={field.value}
                          />
                        )
                      }}
                    />
                  </S.FormField>
                )}
                {viewBy === ViewBy.OVS && (
                  <S.FormField>
                    {labelField('Status OV', 'codigosStatusOrdemVenda')}
                    <Controller
                      control={control}
                      name="codigosStatusOrdemVenda"
                      render={({ field }) => {
                        const inputValue = filterSelectedOV(field.value)
                        return (
                          <Multiselect
                            disabled={isDisabledFilter('codigosStatusOrdemVenda')}
                            height={22}
                            onChange={(value) => field.onChange(value)}
                            optionsList={filteredStatusOvKeys}
                            selectedOptions={JSON.parse(JSON.stringify(inputValue))}
                          />
                        )
                      }}
                    />
                  </S.FormField>
                )}
                <S.FormField>
                  {labelField('Personalização', 'materialMTO')}
                  <Controller
                    control={control}
                    name="materialMTO"
                    render={({ field }) => (
                      <Select
                        defaultValue={0}
                        disabled={disabledsButtons.includes('materialMTO')}
                        height={22}
                        name="materialMTO"
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        options={materialType}
                        value={field.value}
                      />
                    )}
                  />
                </S.FormField>
                {viewBy === ViewBy.OVS && (
                  <>
                    <S.FormField>
                      {labelField('NF', 'numeroNotaFiscal')}
                      <Controller
                        control={control}
                        name="numeroNotaFiscal"
                        render={({ field }) => (
                          <BatchSearch
                            disabled={isDisabledFilter('numeroNotaFiscal')}
                            height={22}
                            onClear={() => {
                              reset({ ...getValues(), numeroNotaFiscal: '' })
                              restoreStructure()
                            }}
                            placeHolder="___ . ___ . ___"
                            {...field}
                          />
                        )}
                      />
                    </S.FormField>
                    <S.FormField>
                      {labelField('Doc. Fatura', 'documentoFatura')}
                      <Controller
                        control={control}
                        name="documentoFatura"
                        render={({ field }) => (
                          <BatchSearch
                            disabled={isDisabledFilter('documentoFatura')}
                            height={22}
                            onClear={() => {
                              reset({ ...getValues(), documentoFatura: '' })
                              restoreStructure()
                            }}
                            placeHolder="___ . ___ . ___"
                            {...field}
                          />
                        )}
                      />
                    </S.FormField>
                  </>
                )}
                <S.FormField>
                  {labelField('Origem do Pedido', 'origem')}
                  <Controller
                    control={control}
                    name="origem"
                    render={({ field }) => {
                      return (
                        <Multiselect
                          height={22}
                          labelOptionsList={originOrderOptions.reduce(
                            (acc, origin) => {
                              acc[origin.value] = origin.title
                              return acc
                            },
                            {} as Record<string, string>,
                          )}
                          onChange={(value) => field.onChange(value)}
                          optionsList={originOrderOptions.map(
                            (origin) => origin.value as OriginOrder,
                          )}
                          selectedOptions={field.value}
                        />
                      )
                    }}
                  />
                </S.FormField>
              </S.FilterContentRow>

              <S.FilterContentRow>
                <Controller
                  control={control}
                  name="periodo"
                  render={({ field }) => (
                    <IntervalPicker
                      disabled={isPrioritaryFilled}
                      displayAsSelect
                      label="Mês Cota"
                      name={field.name}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  )}
                />

                {viewBy !== ViewBy.OVS && (
                  <S.FormField>
                    {labelField('Criação do Pedido', 'dataCriacaoPedido')}
                    <Controller
                      control={control}
                      name="dataCriacaoPedido"
                      render={({ field }) => {
                        return (
                          <DatePicker
                            {...field}
                            height={22}
                            inputFormat="DD/MM/YYYY"
                            maxDate={dayjs() as unknown as string} // TODO: replace direct dayjs() usage with utils
                            onChange={(date) => field.onChange(date)}
                            placeholder="dd/mm/aaaa"
                          />
                        )
                      }}
                    />
                  </S.FormField>
                )}
                <S.FormField flex>
                  <S.WrapCheckboxField>
                    <S.WrapHierarchyField>
                      <Controller
                        control={control}
                        name="hierarquia"
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            checked={field.value}
                            disabled={!codSapClie}
                            label="Hierarquia"
                            size={'small'}
                          />
                        )}
                      />
                      <Info iconSize={12}>{i18n.t('common.messages.hierarquiaInfo')}</Info>
                    </S.WrapHierarchyField>
                    <Controller
                      control={control}
                      name="caracteristicas"
                      render={({ field }) => {
                        return (
                          <>
                            {getFilteredCaracteristicas().map((option) => (
                              <Checkbox
                                bold
                                checked={field.value.includes(option)}
                                key={option}
                                label={option}
                                onChange={(e) => {
                                  const newOptions = e.target.checked
                                    ? [...field.value, option]
                                    : field.value.filter((item) => item !== option)
                                  field.onChange(newOptions)
                                }}
                                size="small"
                              />
                            ))}
                          </>
                        )
                      }}
                    />
                  </S.WrapCheckboxField>
                </S.FormField>
                <S.ClearFiltersWrapper>
                  <SlimButton
                    content="Limpar busca"
                    icon={<Icon icon="eraser" size="20" />}
                    onClick={handleClearFilter}
                    type="button"
                    variant="white"
                  />
                  <SlimButton
                    content="Buscar"
                    disabled={
                      !isPrioritaryFilled &&
                      (!selectedMarkets.length ||
                        !selectedOffices.length ||
                        !selectedRegions.length ||
                        !selectedZones.length)
                    }
                    height={22}
                    type="submit"
                    variant="primary"
                  />
                </S.ClearFiltersWrapper>
              </S.FilterContentRow>
            </S.FilterContent>
          </S.ContainerFilter>

          <SelectionTab
            disabledHideStatusChecked={isPrioritaryFilled}
            setViewBy={setViewBy}
            viewBy={viewBy}
          />
        </FormProvider>
      </S.Container>
    </>
  )
}
